// src/pages/Reports/IssuanceReporting/ReconciliationDiscrepancy/CreateDiscrepancyModal.tsx
// RPT-010 (README.md mục 7) — ghi nhận thủ công 1 chênh lệch phát hiện được. "Lệch khi đối soát"
// (giá trị 4) không cho chọn tay ở đây — chỉ BE tự tạo từ ConfirmAgentReconciliationCommand.

import React, { useState } from 'react';
import { Button, Col, Form, FormGroup, Input, Label, Modal, ModalBody, ModalHeader, Row } from 'reactstrap';
import { toast } from 'react-toastify';

import { createTicketDiscrepancyApi } from 'helpers/fakebackend_helper';
import NumberInput from 'Components/Common/NumberInput';
import AgentSelect from '../shared/AgentSelect';
import { DISCREPANCY_TYPE_OPTIONS } from '../shared/types';
import { createScreenText } from 'helpers/screenText';
const st = createScreenText('reports_issuance_reporting');

interface CreateDiscrepancyModalProps {
  isOpen: boolean;
  planId: number;
  onClose: () => void;
  onCreated: () => void;
}

const emptyForm = {
  discrepancyType: DISCREPANCY_TYPE_OPTIONS[0].value as number,
  agentId: '',
  symbol: '',
  rangeStart: '',
  rangeEnd: '',
  expectedQuantity: 0,
  actualQuantity: 0,
  reason: '',
};

const CreateDiscrepancyModal: React.FC<CreateDiscrepancyModalProps> = ({ isOpen, planId, onClose, onCreated }) => {
  const [form, setForm] = useState(emptyForm);
  const [submitting, setSubmitting] = useState(false);

  const handleToggle = () => {
    if (submitting) return;
    setForm(emptyForm);
    onClose();
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!form.reason.trim()) {
      toast.error(st('vui_long_nhap_ly_do_chenh_message'));
      return;
    }
    setSubmitting(true);
    try {
      await createTicketDiscrepancyApi({
        issuancePlanId: planId,
        discrepancyType: form.discrepancyType,
        agentId: form.agentId || undefined,
        symbol: form.symbol.trim() || undefined,
        rangeStart: form.rangeStart.trim() || undefined,
        rangeEnd: form.rangeEnd.trim() || undefined,
        expectedQuantity: form.expectedQuantity,
        actualQuantity: form.actualQuantity,
        reason: form.reason.trim(),
      });
      toast.success(st('ghi_nhan_chenh_lech_thanh_cong_message'));
      setForm(emptyForm);
      onCreated();
    } catch (error: any) {
      toast.error(error?.message || st('khong_the_ghi_nhan_chenh_lech_message'));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal isOpen={isOpen} toggle={handleToggle} centered size="lg">
      <ModalHeader className="bg-light p-3" toggle={handleToggle}>{st('ghi_nhan_chenh_lech_title')}</ModalHeader>
      <Form onSubmit={handleSubmit}>
        <ModalBody>
          <Row>
            <Col md={6}>
              <FormGroup>
                <Label>{st('loai_chenh_lech_label')} <span className="text-danger">*</span></Label>
                <Input
                  type="select"
                  value={form.discrepancyType}
                  onChange={(e) => setForm((prev) => ({ ...prev, discrepancyType: Number(e.target.value) }))}
                >
                  {DISCREPANCY_TYPE_OPTIONS.map((opt) => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </Input>
              </FormGroup>
            </Col>
            <Col md={6}>
              <FormGroup>
                <Label>{st('dai_ly_lien_quan_label')}</Label>
                <AgentSelect value={form.agentId} onChange={(v) => setForm((prev) => ({ ...prev, agentId: v }))} />
              </FormGroup>
            </Col>
          </Row>
          <Row>
            <Col md={4}>
              <FormGroup>
                <Label>{st('ky_hieu_label')}</Label>
                <Input type="text" value={form.symbol} onChange={(e) => setForm((prev) => ({ ...prev, symbol: e.target.value }))} />
              </FormGroup>
            </Col>
            <Col md={4}>
              <FormGroup>
                <Label>{st('tu_so_column')}</Label>
                <Input type="text" value={form.rangeStart} onChange={(e) => setForm((prev) => ({ ...prev, rangeStart: e.target.value }))} />
              </FormGroup>
            </Col>
            <Col md={4}>
              <FormGroup>
                <Label>{st('den_so_column')}</Label>
                <Input type="text" value={form.rangeEnd} onChange={(e) => setForm((prev) => ({ ...prev, rangeEnd: e.target.value }))} />
              </FormGroup>
            </Col>
          </Row>
          <Row>
            <Col md={6}>
              <FormGroup>
                <Label>{st('so_luong_du_kien_label')} <span className="text-danger">*</span></Label>
                <NumberInput required value={form.expectedQuantity} onChange={(val) => setForm((prev) => ({ ...prev, expectedQuantity: val }))} />
              </FormGroup>
            </Col>
            <Col md={6}>
              <FormGroup>
                <Label>{st('so_luong_thuc_te_label')} <span className="text-danger">*</span></Label>
                <NumberInput required value={form.actualQuantity} onChange={(val) => setForm((prev) => ({ ...prev, actualQuantity: val }))} />
              </FormGroup>
            </Col>
          </Row>
          <FormGroup>
            <Label>{st('ly_do_label')} <span className="text-danger">*</span></Label>
            <Input
              required
              type="textarea"
              rows={3}
              value={form.reason}
              onChange={(e) => setForm((prev) => ({ ...prev, reason: e.target.value }))}
            />
          </FormGroup>
        </ModalBody>
        <div className="modal-footer">
          <div className="hstack gap-2 justify-content-end">
            <Button color="light" onClick={handleToggle} disabled={submitting}>{st('huy_column')}</Button>
            <Button color="success" type="submit" disabled={submitting}>
              {submitting && <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>}
              {st('ghi_nhan_button')}
            </Button>
          </div>
        </div>
      </Form>
    </Modal>
  );
};

export default CreateDiscrepancyModal;
